import React from "react"
import Toggle from "material-ui/Toggle"
import SelectField from "material-ui/SelectField"
import MenuItem from "material-ui/MenuItem"
import { Card, CardHeader, CardText } from "material-ui/Card"
import strings from "../../localization/strings"
import AuthenticationStore from "../../stores/AuthenticationStore"
import HierarchySettingsStore from "../../stores/HierarchySettingsStore"
import autobind from "class-autobind"
import * as  accountActions from "../../actions/AccountActions"
import * as api from "../../api"
import { IHierarchyItemForAccountLevel, IHierarchyItem, IHierarchyItemSettings, IJobDocument } from "../../models"
import * as utils from "../../helpers/utils"
import * as hierarchyHelper from "../../helpers/hierarchyHelper"
import AppStateStore from "../../stores/AppStateStore"
import AccountStore from "../../stores/AccountStore"
import _ from "lodash"
import HierarchyJobVariations from "./hierarchy_job_variations/HierarchyJobVariations"
import * as payrollHelper from "../../helpers/payrollHelper"
import moment from "moment"
import * as permissionHelper from "../../helpers/permissionHelper"

interface IProps {
    hierarchyJobItem: IHierarchyItemForAccountLevel | IHierarchyItem | IJobDocument
    inheritedHierarchyJobItem: IHierarchyItem | IJobDocument
    readonly: boolean
    isJobSettings: boolean
    handleUpdateJob(job: IJobDocument): Promise<void>
}
interface IState {
    expand: boolean
    isRoot: boolean
    canOverride: boolean
    payGroupId: string
}

export default class HierarchySettingsPay extends React.Component<IProps, IState> {
    constructor(props) {
        super(props)
        this.state = {
            expand: false,
            isRoot: true,
            canOverride: false,
            payGroupId: null
        }
        autobind(this)
    }

    componentDidMount() {
        this.checkIsRoot(this.props.hierarchyJobItem)
        this.getPayGroupId(this.props.inheritedHierarchyJobItem)
    }

    componentWillReceiveProps(nextProps: IProps) {
        !_.isEqual(this.props.hierarchyJobItem, nextProps.hierarchyJobItem) &&
            this.checkIsRoot(nextProps.hierarchyJobItem)
        !_.isEqual(this.props.inheritedHierarchyJobItem, nextProps.inheritedHierarchyJobItem) &&
            this.getPayGroupId(nextProps.inheritedHierarchyJobItem)
    }

    checkIsRoot(hierarchyJobItem: IHierarchyItemForAccountLevel | IHierarchyItem | IJobDocument) {
        hierarchyJobItem = hierarchyJobItem as IHierarchyItemForAccountLevel
        this.setState({ isRoot: hierarchyJobItem && hierarchyJobItem.level && hierarchyJobItem.level === -1 ? true : false }, () =>
            this.getCanOverride()
        )
    }

    getCanOverride() {
        if (!this.state.isRoot) {
            const hierarchyItem = this.props.hierarchyJobItem as (IHierarchyItem | IJobDocument)
            const inheritedHierarchyJobItemId = this.props.inheritedHierarchyJobItem ? this.props.inheritedHierarchyJobItem.Id : null
            this.setState({ canOverride: hierarchyItem.Id === inheritedHierarchyJobItemId })
        }
        else {
            this.setState({ canOverride: false })
        }
    }

    getPayGroupId(inheritedHierarchyItem: IHierarchyItem | IJobDocument) {
        const hierarchySettings = inheritedHierarchyItem ?
            inheritedHierarchyItem.HierarchyItemSettings :
            AccountStore.getCurrentAccount().HierarchyItemSettings
        this.setState({ payGroupId: hierarchySettings.PayGroupSettings.PayGroupId })
    }

    handleSave(canOverride, payGroupId) {
        AppStateStore.setProgressSnackbarContent("loading")
        setTimeout(async () => {
            const newPayGroupId = this.state.isRoot ? payGroupId : canOverride ? payGroupId : ""
            const response = this.props.isJobSettings ?
                await api.checkCanChangeJobPayGroup(AuthenticationStore.getCookieAccountId(), (this.props.hierarchyJobItem as IJobDocument).Id, newPayGroupId) :
                await api.checkCanChangeHierarchyPayGroup(AuthenticationStore.getCookieAccountId(), HierarchySettingsStore.getGlobalLevelItemFilterId(), newPayGroupId)
            AppStateStore.setProgressSnackbarContent(null)
            if (response.data) {
                this.setPayGroup(canOverride, payGroupId)
            } else {
                if (permissionHelper.isGlobalAdmin()) {
                    const oldPayGroup = AccountStore.getCurrentAccount().PayGroups.find((item) => item.Id === this.state.payGroupId)
                    const oldPayrollDates = payrollHelper.getPayrollDates(moment(), oldPayGroup.Id)
                    const oldPayGroupName = `${oldPayGroup.Name} (${moment(oldPayrollDates.StartDate).format("ddd MMM. DD, YYYY")} - ${moment(oldPayrollDates.EndDate).format("ddd MMM. DD, YYYY")})`
                    const newPayGroup = AccountStore.getCurrentAccount().PayGroups.find((item) => item.Id === payGroupId)
                    const newPayrollDates = payrollHelper.getPayrollDates(moment(), newPayGroup.Id)
                    const newPayGroupName = `${newPayGroup.Name} (${moment(newPayrollDates.StartDate).format("ddd MMM. DD, YYYY")} - ${moment(newPayrollDates.EndDate).format("ddd MMM. DD, YYYY")})`

                    AppStateStore.setConfirmDialogContent({
                        title: strings.Warning,
                        confirmBtnLabel: strings.Proceed,
                        content: strings.formatString(strings.warning_change_pay_group_context_as_global_admin, oldPayGroupName, newPayGroupName) as string,
                        confirm: () => this.setPayGroup(canOverride, payGroupId)
                    })
                }
                else {
                    AppStateStore.setConfirmDialogContent({
                        title: strings.Warning,
                        confirmBtnLabel: strings.contact_support,
                        content: strings.warning_change_pay_group_context,
                        confirm: () => {
                            window.location.href = "mailto:support@findd.io?subject=Change PayGroup Requirement&body=Please write your requirement in here.";
                        }
                    })
                }
            }
        }, 1000)
    }

    setPayGroup(canOverride: boolean, payGroupId: string) {
        this.setState({ canOverride, payGroupId }, () =>
            this.state.isRoot ?
                this.updateAccountPayGroupSettings() :
                this.updateHierarchyJobItemPayGroupSettings()
        )
    }

    updateAccountPayGroupSettings() {
        let hierarchyItemSettings = AccountStore.getCurrentAccount().HierarchyItemSettings
        if (hierarchyItemSettings) {
            hierarchyItemSettings.PayGroupSettings = { PayGroupId: this.state.payGroupId }
        } else {
            hierarchyItemSettings = { PayGroupSettings: { PayGroupId: this.state.payGroupId } } as IHierarchyItemSettings
        }
        accountActions.patchAccountHierarchySettings(AccountStore.getCurrentAccount().Id, { HierarchyItemSettings: hierarchyItemSettings }, utils.guid())
    }

    updateHierarchyJobItemPayGroupSettings() {
        const hierarchyJobItem = (this.props.hierarchyJobItem) as (IHierarchyItem | IJobDocument)
        if (this.state.canOverride) {
            if (hierarchyJobItem.HierarchyItemSettings) {
                hierarchyJobItem.HierarchyItemSettings.PayGroupSettings = { PayGroupId: this.state.payGroupId }
            } else {
                hierarchyJobItem.HierarchyItemSettings = { PayGroupSettings: { PayGroupId: this.state.payGroupId } } as IHierarchyItemSettings
            }
        } else {
            if (hierarchyJobItem.HierarchyItemSettings) {
                hierarchyJobItem.HierarchyItemSettings.PayGroupSettings = null
            } else {
                hierarchyJobItem.HierarchyItemSettings = { PayGroupSettings: null } as IHierarchyItemSettings
            }
        }
        hierarchyJobItem.hasOwnProperty("AccountId") ?
            this.props.handleUpdateJob(hierarchyJobItem as IJobDocument).then(() => AppStateStore.setProgressSnackbarContent(null)) :
            accountActions.updateAccountHierarchyItem(AccountStore.getCurrentAccount().Id, hierarchyJobItem, true, utils.guid())
    }

    render() {
        return (
            <Card
                data-intercom-target={"menu-Hierarchy-Settings-Pay"}
                expanded={this.state.expand}
                onExpandChange={expand => this.setState({ expand })}
            >
                <CardHeader
                    title={strings.Pay}
                    subtitle={[
                        <span key="title">
                            {this.state.isRoot ?
                                `(${strings.not_inherited})` :
                                !this.props.inheritedHierarchyJobItem ?
                                    `(${strings.formatString(strings.inherited_from_Account_x, AccountStore.getCurrentAccount().Name)})` :
                                    this.props.inheritedHierarchyJobItem.Id === (this.props.hierarchyJobItem as (IHierarchyItem | IJobDocument)).Id ?
                                        `(${strings.not_inherited})` :
                                        `(${strings.formatString(strings.inherited_from_x, `${hierarchyHelper.getHierarchyDefinitionByHierarchyItemId(this.props.inheritedHierarchyJobItem.Id).Label}:  ${(this.props.inheritedHierarchyJobItem as IHierarchyItem).Label}`)})`
                            }
                        </span>,
                        this.state.expand &&
                        <HierarchyJobVariations
                            key="hierarchy job variations"
                            settingPropertyName="PayGroupSettings"
                            hierarchyJobItem={this.props.hierarchyJobItem}
                            inheritedHierarchyJobItem={this.props.inheritedHierarchyJobItem}
                        />
                    ]}
                    actAsExpander
                    showExpandableButton
                    titleStyle={{ fontSize: 20, fontWeight: 500, display: "initial" }}
                    subtitleStyle={{ fontSize: 13, color: "rgb(97, 97, 97)", marginLeft: 10, display: "initial" }}
                />
                <CardText expandable>
                    {!this.state.isRoot &&
                        <Toggle
                            data-intercom-target={"menu-Hierarchy-Settings-Pay-Toggle-Override"}
                            label={strings.Override}
                            style={{ marginBottom: 5, width: 250 }}
                            onToggle={(e, canOverride) =>
                                AppStateStore.setConfirmDialogContent({
                                    content: strings.Are_you_sure_you_want_to_change_this_setting_of_hierarchy,
                                    confirm: () => canOverride ?
                                        this.setState({ canOverride }, () => this.updateHierarchyJobItemPayGroupSettings()) :
                                        this.handleSave(canOverride, this.state.payGroupId)
                                })
                            }
                            toggled={this.state.canOverride}
                            disabled={this.props.readonly}
                        />
                    }
                    <SelectField
                        data-intercom-target={"menu-Hierarchy-Settings-Pay-Select-PayGroup"}
                        floatingLabelText={strings.Pay_Group}
                        style={{ width: 250 }}
                        value={this.state.payGroupId}
                        onChange={(event, key, payGroupId) =>
                            AppStateStore.setConfirmDialogContent({
                                content: strings.Are_you_sure_you_want_to_change_this_setting_of_hierarchy,
                                confirm: () => this.state.payGroupId !== payGroupId &&
                                    this.handleSave(this.state.canOverride, payGroupId)
                            })
                        }
                        disabled={(this.state.isRoot || this.state.canOverride) && !this.props.readonly ? false : true}
                    >
                        {AccountStore.getCurrentAccount().PayGroups.map((item, key) =>
                            <MenuItem
                                value={item.Id}
                                primaryText={item.Name}
                                key={key}
                            />
                        )}
                    </SelectField>
                    {AccountStore.getEnablePayGroupAssignEmployeeLevel() &&
                        <div>
                            <span style={{ fontWeight: 500, fontSize: 15, marginRight: 10 }}>{strings.Pay_Group}:</span>
                            <span>
                                {this.props.isJobSettings ?
                                    strings.Job_Settings_PayGroup_Note :
                                    strings.Hierarchy_Settings_PayGroup_Note
                                }
                            </span>
                        </div>
                    }

                </CardText>
            </Card>
        )
    }
}